<template>
    <section class="property-summary-title">
        <h1 class="singleline">
            <span class="text-bold">{{ dpp.geo.address }},</span>
            <a v-if="dpp.geo.city" class="hover-dotted"  :href="glb.appUrl + dpp.geo.cityPath" :title="`${dpp.geo.city}, ${dpp.geo.state} Homes for Sale`">{{ dpp.geo.city }}, {{ dpp.geo.state }}</a>
            <a v-if="dpp.geo.zipcode" class="hover-dotted" :href="glb.appUrl + dpp.geo.zipcodePath" :title="`${dpp.geo.zipcode}, ${dpp.geo.state} Real Estate and Homes for Sale`">{{ dpp.geo.zipcode }}</a>
        </h1>
        <ul :class="{'force-bottom': showSummaryLabels}">
            <li class="property-price">
                <template v-if="dpp.isRentals">
                    <b v-if="dpp.price">{{$filters.formatPrice(dpp.price)}}<span class="f7">/Mo</span></b>
                    <b v-else>Undisclosed</b>
                </template>
                <template v-else-if="dpp.isActive">
                    <b v-if="dpp.price">
                        {{$filters.formatPrice(dpp.price)}}
                    </b>
                    <b v-else>Undisclosed</b>
                    <p class="property-summary-labels" v-if="showSummaryLabels">
                        <template v-if="getEstimatePriceRange">
                            <span>
                                <i class="icon-logo-m-new f7"></i> <button type="button" class="underdash" v-scroll="'propertyEstimatePanel'">Est: </button>
                                <button type="button" class="link" v-scroll="'propertyEstimatePanel'">
                                   {{getEstimatePriceRange}}
                                </button>
                            </span>
                            &bull;
                        </template>
                        <button type="button" v-scroll="'propertyMortgagePanel'" v-if="getMortgagePerMonth && !hideEstMonthCost"><i class="icon-calculator"></i> <template v-if="!glb.isPhone">Mtg:</template> {{$filters.formatPrice(getMortgagePerMonth)}}/mo</button>
                        <button type="button" class="link underline" @click="showForm(getPreApprovedObj)">{{ getPreApprovedObj.cta }}</button>
                    </p>
                </template>
                <template v-else-if="lessThan12Months">
                    <b v-if="dpp.price">{{$filters.formatPrice(dpp.price)}}</b>
                    <b v-else-if="getEstimateInfo && getEstimateInfo.value > 0"><button class="btn-estimate" type="button" @click="openDisclaimer"></button> {{ $filters.formatPrice(getEstimateInfo.value) }}</b>
                    <b v-else>Undisclosed</b>
                    <p class="property-summary-labels" data-allow-mismatch="text">
                        <span class="text-sentencecase" v-if="dpp.soldDate">Last sold {{$filters.formatDate(dpp.soldDate)}} {{dpp.soldTimeDisplay ? `after ${dpp.soldTimeDisplay}` : ''}}</span>
                        <span class="text-sentencecase" v-else>Last sold date unavailable</span>
                    </p>
                </template>
                <template v-else>
                    <b v-if="getEstimateInfo && getEstimateInfo.value > 0"><button class="btn-estimate" type="button" @click="openDisclaimer"></button> {{ $filters.formatPrice(getEstimateInfo.value) }}</b>
                    <b v-else-if="dpp.price">{{$filters.formatPrice(dpp.price)}}</b>
                    <b v-else>Undisclosed</b>
                    <p class="property-summary-labels" data-allow-mismatch="text">
                        <span class="text-sentencecase" v-if="dpp.soldDate">
                            <template v-if="getEstimateInfo && getEstimateInfo.value">
                                Last sold {{ $filters.formatDate(dpp.soldDate) }} {{ dpp.price ? `for ${$filters.formatFriendlyPrice(dpp.price)}` : ''}}
                            </template>
                            <template v-else>
                                Last sold {{ $filters.formatDate(dpp.soldDate) }} {{ dpp.soldTimeDisplay ? `after ${dpp.soldTimeDisplay}` : ''}}
                            </template>
                        </span>
                        <span class="text-sentencecase" v-else>
                            Last sold date unavailable
                        </span>
                    </p>
                </template>
            </li>
            <li>
                <b>{{ dpp.bed ? dpp.bed : '—' }}</b> <p>{{molang('dpp.dppTitle.bed')}}</p>
            </li>
            <li>
                <b>{{ dpp.bath ? dpp.bath : '—' }}</b> <p>{{molang('dpp.dppTitle.bath')}}</p>
            </li>
            <li v-if="dpp.area">
                <b>{{$filters.formatArea(dpp.area, dpp.isLand) }}</b>
                <p>
                    {{dpp.areaUnit}}
                    <span v-if="dpp.pricePerArea && !hidePricePreArea">({{$filters.formatPricePerArea(dpp.pricePerArea, dpp.isLand, dpp.isRentals)}}/{{dpp.areaUnit}})</span>
                </p>
            </li>
        </ul>
        <div class="property-status" :class="dpp.labelClass" v-if="dpp.labelName" >
            <i></i>
            <span data-allow-mismatch="text" class="text-front">{{formattedLabelName}}</span>
            <template v-if="newSoldPublicRecordCta && !dpp.isActive">
                <a v-if="isDigsSubscribed" class="btn secondary small hidden-sm hidden-md hidden-lg" :href="goToDashboard">Owner Dashboard</a>
                <button type="button" v-else class="btn secondary small hidden-sm hidden-md hidden-lg" @click="openDig()">{{ molang('dpp.hotButton.iAmOwner') }}</button>
            </template>
            <div style="display: flex; align-items: center; gap: var(--gxs);" :class="{'text-green': dpp.isPriceReduced, 'text-red': !dpp.isPriceReduced}" v-if="dpp.showPriceChange && !dpp.hideDaysOnMarket"> <b :class="dpp.isPriceReduced ? 'icon-arrow-down-circle-filled' : 'icon-send-ios'"></b> <span>{{$filters.formatFriendlyPrice(dpp.priceChange)}}</span> {{ dpp.isPriceReduced ? 'price drop' : 'price increase' }}</div>
        </div>
        <div class="hidden-lg hidden-md" v-if="dpp.isNewDpp">
            <dppHotleadButton :orientation="ctaOrientation" :requestTourSubtext="ctaRequestTourSubtext" />
        </div>
        <button v-if="getSplit('movoto-calcpayment-cw-12068')" type="button" class="link bottom-text" @click="show3way()" >
            Do You Qualify for Low Money Down? Check {{ dpp.isNewDpp ? '' : 'FHA ' }}Eligibility
        </button>
        <button v-else-if="showVeteransDrawLink"  class="link bottom-text"  @click="openVeteransHotlead({leadType : 'VeteranDPPSection'})">
            Veterans: Find an agent familiar with $0 down VA loans
        </button>
        <div class="bottom-text" v-if="glb.transactionCount > 10">
            {{glb.transactionCount}} homes bought nearby with <span @click="showTransDialog" class="underdash" role="button">Movoto</span> recently
        </div>
        <div class="bottom-text" v-else-if="glb.agentCount > 5">
            <span @click="showTransDialog" class="underdash" role="button">Movoto</span> is helping {{glb.agentCount}} homebuyers near you
        </div>
        <div class="bottom-text" v-else>
            Learn about {{glb.geo.city}}'s top <span @click="showTransDialog" class="underdash"  role="button">local Real Estate agents</span>
        </div>
    </section>
</template>
<script src="./property-summary-title.js"></script>

<style lang="scss">
@use './property-summary-title.scss' as *;
</style>
